<template>
	<aside
		class="bg-white fixed left-0 top-16 flex flex-col transition-all duration-300 ease-in-out shadow-2xl z-45"
		:style="{
			height: 'calc(100vh - 4rem)',
			width:
				!isCollapsed || (isMobile && showMobileMenu) ? '288px' : '80px',
		}"
		:class="{
			'translate-x-0': isMobile && showMobileMenu,
			'-translate-x-full': isMobile && !showMobileMenu,
			'rounded-r-lg': isMobile && showMobileMenu,
			'lg:translate-x-0': !isMobile,
		}"
	>
		<!-- Navigation Menu -->
		<div class="flex-1 overflow-y-auto py-4 px-3 custom-scrollbar">
			<nav class="space-y-1.5">
				<div
					v-if="!isCollapsed || isMobile"
					class="px-4 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wider"
				>
					Menu
				</div>
				<div v-else class="h-px bg-border mx-2 my-2"></div>

				<div
					v-for="route in routes"
					:key="route.name || route.component"
				>
					<div v-if="route.children" class="space-y-1">
						<button
							:id="`menu-${route.id}`"
							class="w-full flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 group relative overflow-hidden hover:bg-surface"
							:class="{
								'bg-surface text-primary': getChildren(
									route.children,
								).includes($route.name),
								'text-text-secondary': !getChildren(
									route.children,
								).includes($route.name),
								'justify-center px-3': isCollapsed && !isMobile,
							}"
							@click="toggleCollapse(route.id)"
						>
							<div
								class="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							></div>
							<div class="relative flex-shrink-0 z-10">
								<i
									:class="[
										route.icon || 'fas fa-columns',
										'w-5 h-5 transition-all duration-200 text-text-primary group-hover:text-primary',
										{
											'mr-0': isCollapsed && !isMobile,
											'mr-4': !(isCollapsed && !isMobile),
										},
									]"
								></i>
							</div>
							<span
								class="flex-1 text-left font-medium tracking-tight menu-text z-10"
								:class="{ hidden: isCollapsed && !isMobile }"
							>
								{{ route.name || route.component }}
							</span>
							<i
								class="fas fa-chevron-down w-3 h-3 transition-all duration-300 flex-shrink-0 z-10 text-text-secondary group-hover:text-primary"
								:class="{
									'rotate-180': !isMenuCollapsed(route.id),
									hidden: isCollapsed && !isMobile,
								}"
							></i>
						</button>
						<div
							:id="route.id"
							class="ml-4 space-y-1 overflow-hidden transition-all duration-300 ease-in-out"
							:class="{
								'max-h-96 opacity-100': !isMenuCollapsed(
									route.id,
								),
								'max-h-0 opacity-0': isMenuCollapsed(route.id),
								'ml-2': isCollapsed && !isMobile,
							}"
						>
							<router-link
								v-for="r in route.children"
								:key="r.name || r.component"
								:to="{ name: getRouteName(r.component) }"
								class="block"
								@click="closeMobileMenu"
							>
								<div
									class="flex items-center px-4 py-2.5 text-sm rounded-lg transition-all duration-200 group relative overflow-hidden hover:bg-surface"
									:class="{
										'bg-surface text-primary':
											$route.name ===
											getRouteName(r.component),
										'text-text-secondary':
											$route.name !==
											getRouteName(r.component),
										'justify-center px-3':
											isCollapsed && !isMobile,
									}"
								>
									<div
										class="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									></div>
									<div
										class="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-all duration-200 z-10"
										:class="{
											'bg-primary':
												$route.name ===
												getRouteName(r.component),
											'bg-text-secondary group-hover:bg-primary':
												$route.name !==
												getRouteName(r.component),
											'mr-0': isCollapsed && !isMobile,
											'mr-3': !(isCollapsed && !isMobile),
										}"
									></div>
									<span
										class="font-medium tracking-tight menu-text z-10"
										:class="{
											hidden: isCollapsed && !isMobile,
										}"
									>
										{{ r.name || r.component }}
									</span>
								</div>
							</router-link>
						</div>
					</div>
					<router-link
						v-else
						:to="{ name: getRouteName(route.component) }"
						class="block"
						@click="closeMobileMenu"
					>
						<div
							class="flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 group relative overflow-hidden hover:bg-surface"
							:class="{
								'bg-surface text-primary':
									$route.name ===
									getRouteName(route.component),
								'text-text-secondary':
									$route.name !==
									getRouteName(route.component),
								'justify-center px-3': isCollapsed && !isMobile,
							}"
						>
							<div
								class="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							></div>
							<div class="relative flex-shrink-0 z-10">
								<i
									:class="[
										route.icon || 'fas fa-columns',
										'w-5 h-5 transition-all duration-200 text-text-primary group-hover:text-primary',
										{
											'mr-0': isCollapsed && !isMobile,
											'mr-4': !(isCollapsed && !isMobile),
										},
									]"
								></i>
								<div
									v-if="route.component === 'Notification'"
									class="absolute -top-1 -right-1 w-2 h-2 bg-accent rounded-full animate-pulse ring-2 ring-white"
								></div>
							</div>
							<span
								class="flex items-center gap-3 font-medium tracking-tight menu-text z-10 flex-1"
								:class="{ hidden: isCollapsed && !isMobile }"
							>
								{{ route.name || route.component }}
							</span>
						</div>
					</router-link>
				</div>
			</nav>
		</div>

		<!-- Bottom Section -->
		<div class="flex-shrink-0 border-t border-border">
			<!-- Back to Home Link -->
			<a
				href="/"
				class="flex items-center px-4 py-3 text-sm font-medium transition-all duration-200 group relative overflow-hidden hover:bg-surface border-b border-border"
				:class="{ 'justify-center': isCollapsed && !isMobile }"
			>
				<div
					class="absolute inset-0 bg-brand-cyan/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
				></div>
				<div class="relative flex-shrink-0 z-10">
					<i
						:class="[
							'fas fa-home w-5 h-5 transition-all duration-200 text-text-primary group-hover:text-brand-cyan',
							{
								'mr-0': isCollapsed && !isMobile,
								'mr-4': !(isCollapsed && !isMobile),
							},
						]"
					></i>
				</div>
				<span
					class="font-medium tracking-tight text-text-secondary group-hover:text-brand-cyan z-10"
					:class="{ hidden: isCollapsed && !isMobile }"
				>
					Back to Home
				</span>
				<i
					v-if="!isCollapsed || isMobile"
					class="fas fa-external-link-alt ml-auto text-xs text-text-secondary group-hover:text-brand-cyan transition-colors z-10"
				></i>
			</a>

			<!-- User Profile Section (when expanded) -->
			<div
				v-if="!isCollapsed || isMobile"
				class="px-4 py-4 bg-background"
			>
				<div
					class="flex items-center space-x-3 p-3 rounded-xl bg-surface hover:bg-gray-100 transition-all duration-200 cursor-pointer group"
				>
					<div class="relative">
						<div
							class="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold text-sm ring-2 ring-border"
						>
							{{ userInitials }}
						</div>
						<div
							class="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"
						></div>
					</div>
					<div class="flex-1 min-w-0">
						<div
							class="text-text-primary font-medium text-sm truncate"
						>
							{{ userName }}
						</div>
						<div class="text-text-secondary text-xs">Active</div>
					</div>
					<i
						class="fas fa-chevron-right text-text-secondary group-hover:text-primary transition-colors text-sm"
					></i>
				</div>
			</div>

			<!-- Collapsed User Icon -->
			<div v-else class="px-3 py-4 bg-background">
				<div
					class="relative mx-auto w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold text-sm ring-2 ring-border cursor-pointer hover:ring-primary-light transition-all"
				>
					{{ userInitials }}
					<div
						class="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-background"
					></div>
				</div>
			</div>
		</div>
	</aside>
</template>

<script>
import { mapGetters } from 'vuex';
import { version } from '../../../package.json';

export default {
	name: 'SideNav',
	props: {
		isMobile: {
			type: Boolean,
			default: false,
		},
		isCollapsed: {
			type: Boolean,
			default: false,
		},
		showMobileMenu: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			version,
			build_number: process.env.VUE_APP_BUILD_NUMBER || '0',
			collapsedItems: {},
			routes: [
				{
					icon: 'fas fa-chart-pie',
					component: 'Dashboard',
					name: 'Dashboard',
				},
				{
					icon: 'fas fa-wallet',
					component: 'Accounts',
					name: 'Wallet',
				},
				{
					icon: 'fas fa-file-invoice-dollar',
					component: 'Invoice',
					name: 'Invoice',
				},
				{
					icon: 'fas fa-shield-alt',
					component: 'UserProducts',
					name: 'My Policies',
				},
				{
					icon: 'fas fa-quote-left',
					component: 'Quotations',
					name: 'Quotations',
				},
				{
					icon: 'fas fa-balance-scale',
					component: 'Valuation',
					name: 'Valuation',
				},
				{
					icon: 'fas fa-user-circle',
					name: 'Profile',
					children: [
						{ component: 'UserProfile', name: 'User Profile' },
						{ component: 'UserKYC', name: 'KYC Verification' },
					],
					id: 'profile',
				},
				{
					icon: 'fas fa-calendar-alt',
					component: 'Claims',
					name: 'My Claims',
				},
				{
					icon: 'fas fa-bell',
					component: 'Notification',
					name: 'Notifications',
				},
				{
					icon: 'fas fa-calendar-alt',
					component: 'Appointment',
					name: 'Appointments',
				},
			],
		};
	},
	computed: {
		...mapGetters('invoices', ['selectInvoicesData']),
		...mapGetters('kyc', ['selectKycData']),
		userName() {
			return (
				(this.selectKycData &&
					this.selectKycData.names &&
					this.selectKycData.names.firstName) ||
				'User'
			);
		},
		userInitials() {
			return this.userName.charAt(0).toUpperCase();
		},
	},
	mounted() {
		this.routes.forEach((route) => {
			if (route.children && route.id) {
				this.$set(this.collapsedItems, route.id, true);
			}
		});
	},
	methods: {
		closeMobileMenu() {
			if (this.isMobile && this.showMobileMenu) {
				this.$emit('update:showMobileMenu', false);
			}
		},
		toggleCollapse(id) {
			this.collapsedItems = {
				...this.collapsedItems,
				[id]: !this.collapsedItems[id],
			};
		},
		isMenuCollapsed(id) {
			return !!this.collapsedItems[id];
		},
		getChildren(children) {
			return children.map((e) => e.component);
		},
		getRouteName(component) {
			const isNewPortal = this.$route.path.startsWith('/newportal');
			if (isNewPortal) {
				const routeMap = {
					Dashboard: 'NewPortalDashboard',
					Accounts: 'NewPortalAccounts',
					Invoice: 'NewPortalInvoice',
					UserProducts: 'NewPortalUserProducts',
					Quotations: 'NewPortalQuotations',
					UserProfile: 'NewPortalUserProfile',
					UserKYC: 'NewPortalUserKYC',
					Notification: 'NewPortalNotification',
					Appointment: 'NewPortalAppointments',
					Valuation: 'NewPortalValuation',
					Claims: 'NewPortalClaims',
				};
				return routeMap[component] || component;
			}
			return component;
		},
		getUnpaidInvoices() {
			const invoices = this.selectInvoicesData.filter(
				(invoice) => invoice.invoiceStatus == 'PENDING-PAYMENT',
			);
			return invoices.length;
		},
		getPaidInvoices() {
			const invoices = this.selectInvoicesData.filter(
				(invoice) => invoice.invoiceStatus == 'PAID',
			);
			return invoices.length;
		},
	},
};
</script>

<style scoped>
.z-45 {
	z-index: 45;
}

.custom-scrollbar {
	scrollbar-width: thin;
	scrollbar-color: #78758b transparent;
}

.custom-scrollbar::-webkit-scrollbar {
	width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
	background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
	background: #78758b;
	border-radius: 9999px;
	transition: background-color 0.2s;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
	background: #160d50;
}

.menu-text {
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.router-link-exact-active > div {
	animation: slideIn 0.3s ease-out;
}

.group:hover {
	transform: translateX(2px);
}

button:focus-visible,
.router-link-active:focus-visible,
a:focus-visible {
	outline: 2px solid #0a0d57;
	outline-offset: 2px;
	border-radius: 0.75rem;
}

@keyframes slideIn {
	from {
		transform: translateX(-10px);
		opacity: 0;
	}
	to {
		transform: translateX(0);
		opacity: 1;
	}
}

@keyframes subtle-pulse {
	0%,
	100% {
		opacity: 1;
	}
	50% {
		opacity: 0.6;
	}
}

.backdrop-blur-sm {
	backdrop-filter: blur(12px);
}
</style>
